
import React, { useState } from 'react';
import { ShoppingBag, Heart, Search, User, Menu, X, Sun, Moon } from 'lucide-react';
import { useApp } from '../AppContext';

const Header: React.FC = () => {
  const { cart, wishlist, theme, toggleTheme, setIsCartOpen, setIsLoginOpen, siteSettings } = useApp();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const cartCount = cart.reduce((acc, item) => acc + item.quantity, 0);

  return (
    <header className="sticky top-0 z-50 w-full bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 transition-colors">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between gap-4">
        {/* Logo */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={() => setIsMobileMenuOpen(true)} className="lg:hidden p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full">
            <Menu className="w-5 h-5" />
          </button>
          <a href="#" className="text-2xl font-bold tracking-tighter flex items-center gap-2">
            <span className="bg-indigo-600 text-white p-1 rounded-lg">D&G</span>
            <span className="hidden sm:inline font-serif italic text-indigo-600 dark:text-indigo-400">{siteSettings.siteName}</span>
          </a>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-6 text-sm font-medium">
          <a href="#" className="hover:text-indigo-600 transition-colors">Home</a>
          <a href="#categories" className="hover:text-indigo-600 transition-colors">Categories</a>
          <a href="#featured" className="hover:text-indigo-600 transition-colors">Featured</a>
          <a href="#new" className="hover:text-indigo-600 transition-colors">New Arrivals</a>
        </nav>

        {/* Search */}
        <div className="hidden md:flex flex-1 max-w-md relative">
          <input
            type="text"
            placeholder="Search products..."
            className="w-full pl-10 pr-4 py-2 bg-slate-100 dark:bg-slate-800 border-none rounded-full text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 sm:gap-4">
          <button onClick={toggleTheme} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors">
            {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5 text-yellow-400" />}
          </button>
          
          <button onClick={() => setIsLoginOpen(true)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors">
            <User className="w-5 h-5" />
          </button>

          <button className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors relative">
            <Heart className="w-5 h-5" />
            {wishlist.length > 0 && (
              <span className="absolute top-0 right-0 w-4 h-4 bg-rose-500 text-white text-[10px] flex items-center justify-center rounded-full border-2 border-white dark:border-slate-900">
                {wishlist.length}
              </span>
            )}
          </button>

          <button onClick={() => setIsCartOpen(true)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors relative">
            <ShoppingBag className="w-5 h-5" />
            {cartCount > 0 && (
              <span className="absolute top-0 right-0 w-4 h-4 bg-indigo-600 text-white text-[10px] flex items-center justify-center rounded-full border-2 border-white dark:border-slate-900">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[100] lg:hidden">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-4/5 max-w-xs bg-white dark:bg-slate-900 shadow-2xl transition-transform">
            <div className="p-4 flex items-center justify-between border-b dark:border-slate-800">
              <span className="text-xl font-bold font-serif text-indigo-600">{siteSettings.siteName}</span>
              <button onClick={() => setIsMobileMenuOpen(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-4 flex flex-col gap-4 text-lg">
              <a href="#" className="font-medium p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">Home</a>
              <a href="#categories" className="font-medium p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">Categories</a>
              <a href="#featured" className="font-medium p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">Featured</a>
              <a href="#new" className="font-medium p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">New Arrivals</a>
              <hr className="dark:border-slate-800" />
              <div className="px-2">
                <p className="text-xs text-slate-400 uppercase font-bold mb-4">Customer Service</p>
                <a href="#" className="block py-2 text-sm">Contact Us</a>
                <a href="#" className="block py-2 text-sm">Order Tracking</a>
                <a href="#" className="block py-2 text-sm">Returns & Refunds</a>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
